import React, { useState, useEffect } from 'react';
import { Eye, Check, RotateCcw, Lock, AlertTriangle, ChevronDown, ChevronUp, Paintbrush, Search, MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600' },
  finalized_by_customer: { label: 'Finalized', color: 'bg-green-100 text-green-700' },
  under_staff_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700' },
  revisions_requested: { label: 'Revisions', color: 'bg-amber-100 text-amber-700' },
  proof_ready: { label: 'Proof Ready', color: 'bg-cyan-100 text-cyan-700' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  locked: { label: 'Locked', color: 'bg-gray-200 text-gray-600' },
};

const AdminDesignReviews = () => {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [revisionNotes, setRevisionNotes] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const loadSessions = async () => {
    try {
      const url = filter
        ? `${API}/api/design/admin/sessions?status=${filter}`
        : `${API}/api/design/admin/sessions`;
      const res = await axios.get(url, { headers });
      setSessions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) loadSessions(); }, [token, filter]);

  const handleAction = async (sessionId, action, notes = null) => {
    setActionLoading(sessionId);
    try {
      const body = { action };
      if (notes) body.notes = notes;
      await axios.put(`${API}/api/design/admin/session/${sessionId}/review`,
        body, { headers: { ...headers, 'Content-Type': 'application/json' } });
      setRevisionNotes('');
      loadSessions();
    } catch (err) {
      alert(err.response?.data?.detail || `Action failed: ${action}`);
    } finally {
      setActionLoading('');
    }
  };

  const getAvailableActions = (status) => {
    switch (status) {
      case 'finalized_by_customer':
        return [
          { action: 'start_review', label: 'Start Review', icon: Eye, color: 'bg-blue-500 hover:bg-blue-600' },
          { action: 'approve', label: 'Approve', icon: Check, color: 'bg-emerald-500 hover:bg-emerald-600' },
          { action: 'request_revisions', label: 'Request Revisions', icon: RotateCcw, color: 'bg-amber-500 hover:bg-amber-600', needsNotes: true },
        ];
      case 'under_staff_review':
        return [
          { action: 'mark_proof_ready', label: 'Mark Proof Ready', icon: Check, color: 'bg-cyan-500 hover:bg-cyan-600' },
          { action: 'approve', label: 'Approve', icon: Check, color: 'bg-emerald-500 hover:bg-emerald-600' },
          { action: 'request_revisions', label: 'Request Revisions', icon: RotateCcw, color: 'bg-amber-500 hover:bg-amber-600', needsNotes: true },
        ];
      case 'proof_ready':
        return [
          { action: 'approve', label: 'Approve', icon: Check, color: 'bg-emerald-500 hover:bg-emerald-600' },
        ];
      case 'approved':
        return [
          { action: 'lock', label: 'Lock Design', icon: Lock, color: 'bg-gray-600 hover:bg-gray-700' },
        ];
      default:
        return [];
    }
  };

  const counts = sessions.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <AdminLayout>
      <div data-testid="admin-design-reviews">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">Design Reviews</h1>
            <p className="text-sm text-gray-400 mt-1">Review and manage customer design submissions</p>
          </div>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          <button onClick={() => setFilter('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${!filter ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            data-testid="filter-all">
            All ({sessions.length})
          </button>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            counts[key] ? (
              <button key={key} onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${filter === key ? 'bg-[#1B3A5C] text-white' : `${cfg.color} hover:opacity-80`}`}
                data-testid={`filter-${key}`}>
                {cfg.label} ({counts[key]})
              </button>
            ) : null
          ))}
        </div>

        {/* Sessions list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#4A90A4] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
            <Paintbrush className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No Design Sessions</h3>
            <p className="text-sm text-gray-400">Customer design submissions will appear here for review.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="sessions-list">
            {sessions.map(s => {
              const isExpanded = expanded === s.id;
              const statusCfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.draft;
              const actions = getAvailableActions(s.status);
              return (
                <div key={s.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                  data-testid={`session-${s.id}`}>
                  {/* Header row */}
                  <button onClick={() => setExpanded(isExpanded ? null : s.id)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-[#1B3A5C]/5 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Paintbrush className="w-5 h-5 text-[#1B3A5C]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-[#1B3A5C] truncate">
                          {s.main_text || 'Untitled Design'}
                        </p>
                        <p className="text-xs text-gray-400">
                          Order: {s.order_id?.slice(0, 8)}... | v{s.version} | {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusCfg.color}`}>
                        {statusCfg.label}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-gray-50">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
                        {/* Design details */}
                        <div className="lg:col-span-2 space-y-3">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Design Config</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-xs text-gray-400">Layout</span>
                              <p className="font-medium text-[#1B3A5C]">{s.layout_template_id || '—'}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-xs text-gray-400">Font</span>
                              <p className="font-medium text-[#1B3A5C]">{s.font_id || '—'}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-xs text-gray-400">Main Text</span>
                              <p className="font-medium text-[#1B3A5C]">{s.main_text || '—'}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-xs text-gray-400">Subtext</span>
                              <p className="font-medium text-[#1B3A5C]">{s.subtext || '—'}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-xs text-gray-400">Colors</span>
                              <div className="flex gap-2 mt-1">
                                <div className="w-6 h-6 rounded-full border border-gray-200" style={{ backgroundColor: s.primary_color }} title={s.primary_color} />
                                <div className="w-6 h-6 rounded-full border border-gray-200" style={{ backgroundColor: s.secondary_color }} title={s.secondary_color} />
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-xs text-gray-400">Uppercase</span>
                              <p className="font-medium text-[#1B3A5C]">{s.uppercase ? 'Yes' : 'No'}</p>
                            </div>
                          </div>

                          {/* Validation warnings */}
                          {s.validation_results?.warnings?.length > 0 && (
                            <div className="bg-amber-50 p-3 rounded-lg">
                              <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Validation Warnings
                              </p>
                              {s.validation_results.warnings.map((w, i) => (
                                <p key={i} className="text-xs text-amber-600 ml-4">- {w}</p>
                              ))}
                            </div>
                          )}

                          {/* Staff notes */}
                          {s.validation_results?.staff_notes && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" /> Staff Notes
                              </p>
                              <p className="text-xs text-blue-600 ml-4">{s.validation_results.staff_notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Actions panel */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Actions</h3>
                          <div className="space-y-2">
                            {actions.map(a => (
                              <div key={a.action}>
                                {a.needsNotes ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={revisionNotes}
                                      onChange={e => setRevisionNotes(e.target.value)}
                                      placeholder="Describe what needs to change..."
                                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none h-20 focus:outline-none focus:border-amber-400"
                                      data-testid="revision-notes-input" />
                                    <button onClick={() => handleAction(s.id, a.action, revisionNotes)}
                                      disabled={actionLoading === s.id || !revisionNotes}
                                      className={`w-full flex items-center justify-center gap-2 py-2 text-white rounded-xl text-sm font-medium transition disabled:opacity-40 ${a.color}`}
                                      data-testid={`action-${a.action}-${s.id}`}>
                                      <a.icon className="w-4 h-4" /> {a.label}
                                    </button>
                                  </div>
                                ) : (
                                  <button onClick={() => handleAction(s.id, a.action)}
                                    disabled={actionLoading === s.id}
                                    className={`w-full flex items-center justify-center gap-2 py-2 text-white rounded-xl text-sm font-medium transition disabled:opacity-40 ${a.color}`}
                                    data-testid={`action-${a.action}-${s.id}`}>
                                    <a.icon className="w-4 h-4" /> {a.label}
                                  </button>
                                )}
                              </div>
                            ))}
                            {actions.length === 0 && (
                              <p className="text-xs text-gray-400 text-center py-4">
                                No actions available for {statusCfg.label} status
                              </p>
                            )}
                          </div>

                          {/* Timestamps */}
                          <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400 space-y-1">
                            {s.finalized_at && <p>Finalized: {new Date(s.finalized_at).toLocaleString()}</p>}
                            {s.approved_at && <p>Approved: {new Date(s.approved_at).toLocaleString()}</p>}
                            <p>Updated: {s.updated_at ? new Date(s.updated_at).toLocaleString() : '—'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDesignReviews;
